import type { Circuit, PlayerTrackPosition, StartLane, TrackCell } from '../types/track.js';
import { START_LANE_LENGTH, TrackCellType } from '../types/track.js';
import type { ValidationResult } from '../types/track.js';
import { areCellsAdjacent } from './circuitGeometryEdit.js';
import { MAX_TRACK_CELL_OCCUPANCY } from './trackCellOccupancy.js';

export type StartGridMode = 'qual' | 'final' | 'default';

/** Mode de grille selon le type de session championnat. */
export function startGridModeForSessionType(sessionType: string | null | undefined): StartGridMode {
  if (!sessionType) return 'default';
  if (sessionType.startsWith('qual')) return 'qual';
  if (sessionType.startsWith('semi') || sessionType === 'final') return 'final';
  return 'default';
}

export function hasStartLane(circuit: Circuit): boolean {
  return (circuit.startLane?.cells.length ?? 0) === START_LANE_LENGTH;
}

export function createEmptyStartLane(): StartLane {
  return {
    joinPathIndex: -1,
    cells: [],
  };
}

/**
 * Positions de grille au départ.
 * Jamais plus de `MAX_TRACK_CELL_OCCUPANCY` voitures par case en demi/finale / défaut.
 * - 'qual'    : tous les pilotes sur la même case (jonction / S/F) — peloton groupé
 * - 'final'   : demi / finale — 2 pilotes par case ; rang 0-1 en tête de grille
 * - 'default' : 1 pilote par case (course libre)
 */
export function buildStartGridPositions(
  circuit: Circuit,
  numParticipants: number,
  mode: StartGridMode = 'default'
): PlayerTrackPosition[] {
  if (mode === 'qual') {
    const joinIdx = hasStartLane(circuit) ? circuit.startLane!.joinPathIndex : 0;
    const pathLen = Math.max(1, circuit.path.length);
    const mainPathIndex = normalizeJoinIndex(joinIdx, pathLen);
    return Array.from({ length: numParticipants }, () => ({
      mainPathIndex,
      jokerBranchIndex: null,
      startLaneIndex: null,
    }));
  }

  const carsPerCell = mode === 'final' ? MAX_TRACK_CELL_OCCUPANCY : 1;

  if (hasStartLane(circuit)) {
    return placeOnStartLaneThenPath(circuit, numParticipants, carsPerCell);
  }

  const pathLen = Math.max(1, circuit.path.length);
  if (mode === 'final') {
    const numRows = Math.ceil(numParticipants / carsPerCell);
    return Array.from({ length: numParticipants }, (_, rank) => {
      const row = Math.floor(rank / carsPerCell);
      const pathRow = numRows - 1 - row;
      return {
        mainPathIndex: Math.min(pathLen - 1, pathRow),
        jokerBranchIndex: null,
        startLaneIndex: null,
      };
    });
  }

  return Array.from({ length: numParticipants }, (_, rank) => ({
    mainPathIndex: Math.min(pathLen - 1, rank),
    jokerBranchIndex: null,
    startLaneIndex: null,
  }));
}

function normalizeJoinIndex(joinIdx: number, pathLen: number): number {
  if (!Number.isFinite(joinIdx) || pathLen <= 0) return 0;
  const idx = Math.trunc(joinIdx);
  if (idx < 0) return 0;
  return idx % pathLen;
}

/**
 * Remplit d’abord la ligne à `carsPerCell` / case, puis double les cases depuis
 * le fond de grille jusqu’à `MAX_TRACK_CELL_OCCUPANCY`, et seulement ensuite
 * déborde sur le path derrière la jonction.
 */
function placeOnStartLaneThenPath(
  circuit: Circuit,
  numParticipants: number,
  carsPerCell: number
): PlayerTrackPosition[] {
  const joinIdx = circuit.startLane!.joinPathIndex;
  const pathLen = Math.max(1, circuit.path.length);
  const preferredPerCell = Math.min(MAX_TRACK_CELL_OCCUPANCY, Math.max(1, carsPerCell));
  const maxPerCell = MAX_TRACK_CELL_OCCUPANCY;
  const preferredCapacity = START_LANE_LENGTH * preferredPerCell;
  const hardCapacity = START_LANE_LENGTH * maxPerCell;

  return Array.from({ length: numParticipants }, (_, rank) => {
    if (rank < preferredCapacity) {
      return {
        mainPathIndex: joinIdx,
        jokerBranchIndex: null,
        startLaneIndex: Math.floor(rank / preferredPerCell),
      };
    }
    if (rank < hardCapacity) {
      // Doubler depuis le fond (loin de la jonction) : rang preferredCapacity → cell N-1.
      const packRank = rank - preferredCapacity;
      return {
        mainPathIndex: joinIdx,
        jokerBranchIndex: null,
        startLaneIndex: START_LANE_LENGTH - 1 - packRank,
      };
    }
    const overflowRank = rank - hardCapacity;
    const overflowRow = Math.floor(overflowRank / maxPerCell);
    const idx = (joinIdx - 1 - overflowRow + pathLen * (overflowRow + 1)) % pathLen;
    return {
      mainPathIndex: idx,
      jokerBranchIndex: null,
      startLaneIndex: null,
    };
  });
}

export function isOnStartLane(position: PlayerTrackPosition): boolean {
  return position.startLaneIndex != null;
}

export function validateStartLane(circuit: Circuit): ValidationResult {
  const errors: string[] = [];
  const sl = circuit.startLane;
  if (!sl || (sl.joinPathIndex < 0 && sl.cells.length === 0)) {
    return { isValid: true, errors: [] };
  }

  const pathLen = circuit.path.length;
  if (pathLen === 0) {
    errors.push('Le circuit principal doit exister avant d’ajouter une ligne de départ');
    return { isValid: false, errors };
  }

  if (sl.joinPathIndex < 0 || sl.joinPathIndex >= pathLen) {
    errors.push('Point de jonction de la ligne de départ invalide');
  }

  if (sl.cells.length !== START_LANE_LENGTH) {
    errors.push(
      `La ligne de départ doit comporter exactement ${START_LANE_LENGTH} cases (actuellement ${sl.cells.length})`
    );
  }

  if (sl.joinPathIndex >= 0 && sl.cells.length > 0) {
    const join = circuit.path[sl.joinPathIndex];
    const front = sl.cells[0];
    if (!areCellsAdjacent(join.x, join.y, front.x, front.y)) {
      errors.push('La première case de la ligne doit être adjacente au point de jonction');
    }
  }

  for (let i = 1; i < sl.cells.length; i++) {
    const prev = sl.cells[i - 1];
    const cur = sl.cells[i];
    if (!areCellsAdjacent(prev.x, prev.y, cur.x, cur.y)) {
      errors.push('Les cases de la ligne de départ doivent être contiguës');
      break;
    }
  }

  if (sl.cells.length >= 2) {
    const dx = sl.cells[1].x - sl.cells[0].x;
    const dy = sl.cells[1].y - sl.cells[0].y;
    for (let i = 2; i < sl.cells.length; i++) {
      const stepX = sl.cells[i].x - sl.cells[i - 1].x;
      const stepY = sl.cells[i].y - sl.cells[i - 1].y;
      if (stepX !== dx || stepY !== dy) {
        errors.push('La ligne de départ doit être une ligne droite');
        break;
      }
    }
  }

  for (const cell of sl.cells) {
    if (cell.type !== TrackCellType.START_LANE && cell.type !== TrackCellType.START) {
      errors.push('Les cases de la ligne de départ doivent être de type ligne de départ');
      break;
    }
  }

  return { isValid: errors.length === 0, errors };
}

export function applyStartLaneToGrid(
  circuit: Circuit
): import('../types/track.js').TrackCellType[][] {
  const grid = circuit.grid.map((row) => [...row]);
  const cells = circuit.startLane?.cells ?? [];
  for (const cell of cells) {
    if (cell.y >= 0 && cell.y < circuit.height && cell.x >= 0 && cell.x < circuit.width) {
      grid[cell.y][cell.x] = cell.type;
    }
  }
  return grid;
}

export function createStartLaneCell(x: number, y: number, isBackOfGrid: boolean): TrackCell {
  return {
    x,
    y,
    type: isBackOfGrid ? TrackCellType.START : TrackCellType.START_LANE,
  };
}
